import { type ChainflipChain, type ChainflipAsset } from '@chainflip/utils/chainflip';
import { TABLE_POLL_INTERVAL } from '@/shared/constants';
import {
  getFilteredLpAccountsWithBalancesQuery,
  getLpAccountWithBalances,
} from '@/shared/graphql/cache/lp';
import { type GetFilteredLpAccountsWithBalancesQuery } from '@/shared/graphql/generated/cache/graphql';
import { getAllLpsTotalVolumeQuery, getLpsVolumeQuery } from '@/shared/graphql/lp/lp';
import useChainflipAssetPrices from '@/shared/hooks/useChainflipAssetPrices';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import { accountOrdersToLiquidityUsd } from '@/shared/utils/orders';
import { type OpenOrdersCacheResult } from '@/shared/utils/schemas';
import TokenAmount from '@/shared/utils/TokenAmount';
import useSupportedAssets from './useSupportedAssets';

const sumNullishValues = (...values: (string | number | null | undefined)[]) =>
  values.reduce((acc: number, curr) => acc + Number(curr ?? 0), 0);

export const useLpFills = (lpIdSs58?: string, startDate?: Date, endDate?: Date) => {
  const { data: lpsFills, isLoading: lpsFillsLoading } = useGqlQuery(getAllLpsTotalVolumeQuery, {
    refetchInterval: TABLE_POLL_INTERVAL,
    context: { clientName: 'lpProcessor' },
    enabled: !lpIdSs58,
  });

  const { data: lpFills, isLoading: lpFillsLoading } = useGqlQuery(getLpsVolumeQuery, {
    refetchInterval: TABLE_POLL_INTERVAL,
    context: { clientName: 'lpProcessor' },
    variables: {
      idSs58: lpIdSs58!,
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
    },
    enabled: Boolean(lpIdSs58),
  });

  if (!lpIdSs58) {
    const { filledAmountValueUsd } = lpsFills?.limitOrders?.aggregates?.sum ?? {};
    const { baseFilledAmountValueUsd, quoteFilledAmountValueUsd } =
      lpsFills?.rangeOrders?.aggregates?.sum ?? {};
    return {
      lpFills: sumNullishValues(
        filledAmountValueUsd,
        quoteFilledAmountValueUsd,
        baseFilledAmountValueUsd,
      ),
      lpFillsLoading: lpsFillsLoading,
    };
  }

  const filledAmountValueUsd =
    Number(lpFills?.accounts?.nodes[0]?.limitOrders?.aggregates?.sum?.filledAmountValueUsd ?? 0) +
    Number(
      lpFills?.accounts?.nodes[0]?.strategies?.nodes.reduce(
        (acc, curr) =>
          acc + Number(curr.strategy.limitOrders.aggregates?.sum?.filledAmountValueUsd ?? 0),
        0,
      ) ?? 0,
    );
  const { baseFilledAmountValueUsd, quoteFilledAmountValueUsd } =
    lpFills?.accounts?.nodes[0]?.rangeOrders?.aggregates?.sum ?? {};
  return {
    lpFills: sumNullishValues(
      filledAmountValueUsd,
      quoteFilledAmountValueUsd,
      baseFilledAmountValueUsd,
    ),
    lpFillsLoading,
  };
};

type LpBalances = {
  deployedLiquidityUsd: number;
  undeployedLiquidityUsd: number;
  boostLiquidityUsd: number;
  openOrdersLiquidityUsd: number;
  strategiesLiquidityUsd: number;
  lendingSupplyLiquidityUsd: number;
  totalLiquidityUsd: number;
  lpLiquidityPerAsset: Partial<
    Record<
      ChainflipAsset,
      {
        total: TokenAmount;
        totalValueUsd: number;
        undeployed: TokenAmount;
        undeployedValueUsd: number;
        deployed: TokenAmount;
        deployedValueUsd: number;
      }
    >
  >;
};

const aggregateBalances = (
  liquidityData: GetFilteredLpAccountsWithBalancesQuery | undefined,
  assetPrices: Record<ChainflipAsset, number | undefined>,
  supportedAssets?: ChainflipAsset[],
): LpBalances => {
  let strategiesLiquidityUsd = 0;

  liquidityData?.lps?.nodes.forEach((n) => {
    if (n.strategy.aggregates?.sum) {
      strategiesLiquidityUsd +=
        Number(n.strategy.aggregates.sum.baseAssetBalanceUsd) +
        Number(n.strategy.aggregates.sum.quoteAssetBalanceUsd);
    }
  });

  const pools = liquidityData?.pools?.nodes || [];
  const lpOpenOrders = (liquidityData?.lps?.nodes.map((node) => node.openOrders).flat() ??
    []) as OpenOrdersCacheResult[];

  const openOrdersLiquidityUsd = lpOpenOrders.reduce(
    (liquidity, { baseAsset, quoteAsset, orders }: OpenOrdersCacheResult) => {
      const pool = pools.find((p) => p.baseAsset === baseAsset && p.quoteAsset === quoteAsset);
      return (
        liquidity +
        accountOrdersToLiquidityUsd(
          orders,
          baseAsset,
          quoteAsset,
          (assetPrices as Record<ChainflipAsset, number>)[baseAsset] ?? 0,
          (assetPrices as Record<ChainflipAsset, number>)[quoteAsset] ?? 0,
          pool?.rangeOrderPrice ?? 0,
        )
      );
    },
    0,
  );

  const lpBalances = liquidityData?.lps?.nodes.map((node) => node.balances.nodes).flat() ?? [];
  const boostLiquidityUsd = lpBalances.reduce(
    (balanceAcc, item) => balanceAcc + Number(item.boostBalanceValueUsd),
    0,
  );

  const lendingSupplyLiquidityUsd = lpBalances.reduce(
    (balanceAcc, item) => balanceAcc + Number(item.lendingSupplyBalanceValueUsd),
    0,
  );

  const undeployedLiquidityUsd = lpBalances.reduce((acc, curr) => {
    const asset = curr.asset as ChainflipAsset;
    const usdValue =
      TokenAmount.fromAsset(curr.amount, asset)
        ?.mul(assetPrices[asset] ?? 0)
        .toNumber() ?? 0;
    return acc + usdValue;
  }, 0);

  const lpLiquidityPerAsset = lpBalances.reduce<LpStats['lpLiquidityPerAsset']>(
    (acc, curr) => {
      const asset = curr.asset as ChainflipAsset;

      acc[asset] = {
        totalValueUsd: Number(curr.totalBalanceValueUsd),
        total: TokenAmount.fromAsset(curr.totalBalance, asset),
        undeployedValueUsd: Number(curr.valueUsd),
        undeployed: TokenAmount.fromAsset(curr.amount, asset),
        deployedValueUsd: Number(curr.totalBalanceValueUsd) - Number(curr.valueUsd),
        deployed: TokenAmount.fromAsset(curr.totalBalance, asset).sub(
          TokenAmount.fromAsset(curr.amount, asset),
        ),
      };
      return acc;
    },
    supportedAssets
      ? Object.fromEntries(
          supportedAssets.map((asset) => [
            asset,
            {
              totalValueUsd: 0,
              total: TokenAmount.ZERO,
              undeployedValueUsd: 0,
              undeployed: TokenAmount.ZERO,
              deployedValueUsd: 0,
              deployed: TokenAmount.ZERO,
            },
          ]),
        )
      : {},
  );

  const deployedLiquidityUsd =
    openOrdersLiquidityUsd + boostLiquidityUsd + strategiesLiquidityUsd + lendingSupplyLiquidityUsd;

  return {
    deployedLiquidityUsd,
    undeployedLiquidityUsd,
    boostLiquidityUsd,
    openOrdersLiquidityUsd,
    strategiesLiquidityUsd,
    lpLiquidityPerAsset,
    lendingSupplyLiquidityUsd,
    totalLiquidityUsd: deployedLiquidityUsd + undeployedLiquidityUsd,
  };
};

export type RefundAddressMap = Record<ChainflipChain, string | null>;

export function useLpBalancesAndAddresses(lpIdSs58: string | undefined): LpBalances & {
  refundAddresses: RefundAddressMap;
  liquidityDataLoading: boolean;
  hasLiquidity: boolean;
} {
  const { prices: assetPrices } = useChainflipAssetPrices();
  const supportedAssets = useSupportedAssets();

  const { data: liquidityData, isLoading: liquidityDataLoading } = useGqlQuery(
    getLpAccountWithBalances,
    {
      refetchInterval: 5_000,
      context: { clientName: 'statechainCache' },
      variables: { lpIdSs58: lpIdSs58! },
      enabled: Boolean(lpIdSs58),
    },
  );

  const lp = liquidityData?.lpAccount;

  const aggregatedBalances = aggregateBalances(
    liquidityData && {
      pools: liquidityData.pools,
      lps: lp ? { totalCount: 1, nodes: [lp] } : { totalCount: 0, nodes: [] },
    },
    assetPrices,
    supportedAssets,
  );

  return {
    liquidityDataLoading,
    ...aggregatedBalances,
    hasLiquidity: aggregatedBalances.totalLiquidityUsd > 0,
    refundAddresses: {
      Arbitrum: lp?.arbitrumRefundAddress ?? null,
      Assethub: lp?.assethubRefundAddress ?? null,
      Bitcoin: lp?.bitcoinRefundAddress ?? null,
      Ethereum: lp?.ethereumRefundAddress ?? null,
      Solana: lp?.solanaRefundAddress ?? null,
      Tron: lp?.tronRefundAddress ?? null,
      Bsc: lp?.bscRefundAddress ?? null,
    },
  };
}

interface LpStats extends LpBalances {
  volumeFilled: number;
  lpFillsLoading: boolean;
  liquidityDataLoading: boolean;
}

export default function useLpStats(lpIdSs58: string | undefined): LpStats {
  const { prices: assetPrices } = useChainflipAssetPrices();

  const { data: liquidityData, isLoading: liquidityDataLoading } = useGqlQuery(
    getFilteredLpAccountsWithBalancesQuery,
    {
      refetchInterval: 10_000,
      context: { clientName: 'statechainCache' },
      variables: { lpIdSs58 },
    },
  );

  const { lpFills: volumeFilled, lpFillsLoading } = useLpFills(lpIdSs58);

  return {
    ...aggregateBalances(liquidityData, assetPrices),
    liquidityDataLoading,
    volumeFilled,
    lpFillsLoading,
  };
}
